import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Keyboard } from 'react-native';

import { Loading } from '@/components/Loading';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components/Box';
import { ds } from '@/components/lib/design.system';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { useBetslipStore } from '@/feature/betslip-pickem/hooks/use-betslip-store';
import { leagueConfigSelector, useLeagueConfigsStore } from '@/feature/betslip-pickem/hooks/use-league-configs-store';
import { PlayerWithTeamAndEvent } from '@/feature/betslip-pickem/types';
import { createPlayerEventKey } from '@/feature/betslip-pickem/utils/markets-cache-store-utils';
import { useLobbyPlayers } from '@/feature/lobby/hooks/use-fantasy-lobby-query-with-variables';
import { MemoPlayerRow } from '@/feature/lobby/hooks/use-lobby-content-render';
import { usePickSelection } from '@/feature/lobby/hooks/use-pick-selection';
import { useOpenPlayerProfile } from '@/feature/player-profile-sheets/hooks/use-open-player-profile-sheet';
import { common } from '@/styles/styles';
import { League } from '@/types/api.generated';
import { FlashList } from '@shopify/flash-list';
import { useShallow } from 'zustand/react/shallow';

import { usePlayersSearch } from '../hooks/use-players-search';

export const PicksSearchResults = ({
    query,
    league,
    onScrollBeginDrag,
}: {
    query: string;
    league: League;
    onScrollBeginDrag?: () => void;
}) => {
    const { makeSelection, removeSelection } = usePickSelection();
    const leagueLabel = useLeagueConfigsStore(useShallow(leagueConfigSelector(league))).leagueLabel;
    const betslip = useBetslipStore(state => state.betslip);
    const validating = useBetslipStore(state => state.validating);

    const { t } = useTranslation(['search']);
    const trendingPlayersTitle = t('trending_players', { league: leagueLabel || league });

    const { playersToDisplay: searchPlayers, loading } = usePlayersSearch(query, league);
    const { trendingPlayers } = useLobbyPlayers({ requestPolicy: 'cache-only' });
    const leagueSpecificTrendingPlayers = useMemo(() => {
        return trendingPlayers.filter(player => player.league === league);
    }, [league, trendingPlayers]);

    const players: PlayerWithTeamAndEvent[] = useMemo(() => {
        // if there's no query, show the trending players
        return query.length === 0 ? leagueSpecificTrendingPlayers : searchPlayers;
    }, [leagueSpecificTrendingPlayers, query.length, searchPlayers]);

    const { openPlayerProfile } = useOpenPlayerProfile(players);
    const handleOpenPlayerProfile = useCallback(
        (playerId: string, eventId: string) => {
            Keyboard.dismiss();
            openPlayerProfile({ playerId, eventId });
            BetrAnalytics.trackEvent(AnalyticsEvent.SEARCH_PLAYER_SELECTED, {
                league: league,
                trending: query.length === 0,
            });
        },
        [league, openPlayerProfile, query.length]
    );

    const renderItem = useCallback(
        ({ item, index }: { item: PlayerWithTeamAndEvent; index: number }) => {
            const isLastItem = index === players.length - 1;
            return (
                <MemoPlayerRow
                    player={item}
                    renderBottomSeparator={!isLastItem}
                    removeSelection={removeSelection}
                    makeSelection={makeSelection}
                    openPlayerProfile={handleOpenPlayerProfile}
                />
            );
        },
        [handleOpenPlayerProfile, makeSelection, players.length, removeSelection]
    );

    const shouldShowTrending = query.length === 0;

    if (players.length === 0 && loading) {
        return (
            <Box height={'50%'} justifyContent={'center'} alignItems={'center'}>
                <Loading />
            </Box>
        );
    }

    if (shouldShowTrending && players.length === 0) {
        return (
            <Box height={'50%'} justifyContent={'center'} alignItems={'center'} px={'s16'} gap={'s4'}>
                <Text variant={'titleLarge'}>{t('no_trending_players')}</Text>
                <Text variant={'bodyMedium'} textAlign={'center'} color={ds.colorToken.text.secondary}>
                    {t('no_trending_players_subtitle')}
                </Text>
            </Box>
        );
    }

    if (players.length === 0) {
        return (
            <Box height={'50%'} justifyContent={'center'} alignItems={'center'} px={'s16'} gap={'s4'}>
                <Text variant={'titleLarge'}>{t('no_results_found')}</Text>
                <Text variant={'bodyMedium'} textAlign={'center'} color={ds.colorToken.text.secondary}>
                    {t('no_results', { query })}
                </Text>
            </Box>
        );
    }

    return (
        <Box flex={1}>
            <FlashList
                keyboardDismissMode={'on-drag'}
                keyboardShouldPersistTaps={'handled'}
                onScrollBeginDrag={onScrollBeginDrag}
                data={players}
                extraData={JSON.stringify({ ...betslip, validating })}
                ListHeaderComponent={
                    shouldShowTrending ? (
                        <Box mt={'s20'} mb={'s8'}>
                            <Text variant={'headlineSmall'} fontWeight={700}>
                                {trendingPlayersTitle}
                            </Text>
                        </Box>
                    ) : null
                }
                contentContainerStyle={common.paddingHorizontal}
                estimatedItemSize={100}
                keyExtractor={keyExtractor}
                renderItem={renderItem}
            />
        </Box>
    );
};

const keyExtractor = (item: PlayerWithTeamAndEvent, index: number) =>
    `${createPlayerEventKey(item.id, item.event.id)}-${index}`;
